import { useCallback, useEffect, useRef, useState } from 'react';
import { log } from '../../app/logger';
import { completeMatch, loadMatchRecord } from '../../app/matchRecord';
import type { MatchRecordView } from '../../app/matchRecord';
import { loadRoster } from '../../app/roster';
import type { OurPlayerRow } from '../../types';

/**
 * B-18 — the **match record's controller** (design spec §2.4; §6/F-1). Reads, one write, no
 * rendering.
 *
 * ── Every write is followed by a re-read, including the refusals ─────────────
 *
 * The A convention for a facade write is *dispatch, then imperatively re-read* — and on this
 * screen the re-read after a **failure** is the load-bearing half. F-1's guards refuse on facts the
 * screen was showing a moment ago; a draft committed by the workflow behind this record, or a flag
 * pressed on another surface, arrives as a rejection whose only honest response is to show the
 * blocker that caused it. A refusal that left the screen claiming the match was ready to complete
 * would invite the user to press again forever.
 *
 * ── Completion does not navigate ────────────────────────────────────────────
 *
 * A completed match stays on screen, now read-only: the record is the same screen for an archived
 * match (§2.4), and bouncing the user to the home list the instant the match archives would take
 * away the confirmation that anything happened. The Matches home re-reads when this overlay closes,
 * so the Needs-finishing queue is correct by the time it is next visible.
 */

export type RecordPhaseB = 'loading' | 'ready' | 'failed';

const MODULE = 'ui-b/record';

export function useMatchRecord(matchId: string) {
  const [phase, setPhase] = useState<RecordPhaseB>('loading');
  const [view, setView] = useState<MatchRecordView | null>(null);
  /** §18.4's name join — see `OpponentFileScreen` for why every card surface reads this. */
  const [roster, setRoster] = useState<readonly OurPlayerRow[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Completed reads, rendered as `data-read` — a waiter needs an instant, not a sleep. */
  const [reads, setReads] = useState(0);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const reload = useCallback(async (): Promise<void> => {
    try {
      const [next, players] = await Promise.all([loadMatchRecord(matchId), loadRoster()]);
      if (!alive.current) return;
      setView(next);
      setRoster(players);
      setPhase('ready');
    } catch (cause: unknown) {
      const message = cause instanceof Error ? cause.message : String(cause);
      log.error(MODULE, 'the match record could not be read', { matchId, message });
      if (!alive.current) return;
      // A record that cannot be read is not an empty record — say so rather than rendering a
      // match with no observations, which is a legitimate state and looks identical.
      setPhase('failed');
    } finally {
      if (alive.current) setReads((n) => n + 1);
    }
  }, [matchId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** §6 / F-1. The engine owns both guards; this reports whichever one refused. */
  const complete = useCallback((): void => {
    setBusy(true);
    void completeMatch(matchId)
      .then(async (result) => {
        if (!result.ok) {
          if (alive.current) setNotice(result.message);
          // The refusal names a blocker this screen was not showing. Re-read so it is.
          await reload();
          return;
        }
        if (alive.current) setNotice(null);
        await reload();
      })
      .catch((cause: unknown) => {
        const message = cause instanceof Error ? cause.message : String(cause);
        log.error(MODULE, 'the match could not be completed', { matchId, message });
        if (alive.current) setNotice(message);
      })
      .finally(() => {
        if (alive.current) setBusy(false);
      });
  }, [matchId, reload]);

  return {
    state: { phase, view, roster, notice, busy, reads },
    actions: {
      complete,
      /**
       * Returning from a sub-surface that may have resolved the very thing a refusal complained
       * about. The notice is cleared here and NOT inside `reload`, and the difference is the whole
       * point: a refused completion re-reads too, and clearing there would erase the sentence the
       * user has not read yet. Found by the B-18 browser pass, which met a stale
       * "1 flag(s) still need promoting or dismissing" sitting above a record with no flags left.
       */
      refresh: (): void => {
        setNotice(null);
        void reload();
      },
      dismissNotice: (): void => {
        setNotice(null);
      },
    },
  };
}
